import Box from "@mui/joy/Box";
import List from "@mui/joy/List";
import Sheet from "@mui/joy/Sheet";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress, Stack } from "@mui/joy";
import {
  PATIENT_DOMAIN,
  PATIENT_LIST_ENDPOINT,
  patientApi,
  PatientType,
} from "api/patientApi";
import PatientSubList from "./PatientSubList";
import { hasAuthHeader } from "api";
import { patientListMenuKey } from "recoil/menuAtom";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { userAtom } from "recoil/userAtom";
import { SidebarMenuType } from "components/sidebar/SidebarForPatient";

type CategorizedPatientList = {
  listTitle: string;
  patientList: PatientType[];
  menuKey: patientListMenuKey;
};

/**
 * 내담자 목록을 우선순위에 따라 분류하는 헬퍼 함수.
 * 우선순위가 높은 목록에 포함된 내담자는 하위 목록에 중복 표시되지 않습니다.
 * @param patients - API에서 받아온 전체 내담자 목록
 * @returns 분류된 내담자 목록 배열
 */
const categorizePatientsByPriority = (
  patients: PatientType[]
): CategorizedPatientList[] => {
  // 한 번 분류된 내담자의 ID를 저장하여 중복을 방지합니다.
  const assignedPatientIds = new Set<number>();
  const now = new Date();

  // 닉네임 기준으로 먼저 정렬합니다. '전체' 목록에서 사용됩니다.
  const sortedByNickname = [...patients].sort((a, b) =>
    (a.nickname || "") > (b.nickname || "") ? 1 : -1
  );

  // 각 하위 목록은 doneAt 기준으로 정렬하기 위한 헬퍼 함수
  const sortByDoneAt = (list: PatientType[]) =>
    list.sort(
      (a, b) => +new Date(a.doneAt || now) - +new Date(b.doneAt || now)
    );

  /**
   * 특정 조건에 맞는 내담자 목록을 생성하고, 할당된 ID를 기록하는 함수
   * @param filterFn - 내담자를 필터링할 조건 함수
   * @returns 필터링 및 할당 처리된 내담자 목록
   */
  const createPrioritizedList = (
    filterFn: (patient: PatientType) => boolean
  ): PatientType[] => {
    const list: PatientType[] = [];
    for (const patient of sortedByNickname) {
      // 아직 다른 우선순위 높은 목록에 할당되지 않았고, 현재 필터 조건을 만족하는 경우
      if (!assignedPatientIds.has(patient.patientId) && filterFn(patient)) {
        list.push(patient);
        assignedPatientIds.add(patient.patientId); // 할당되었음을 기록
      }
    }
    return sortByDoneAt(list);
  };

  // 1. 채팅 확인 필요 (Priority 1)
  const chatList = createPrioritizedList(
    (p) => (p.unreadMessageCount || 0) > 0
  );
  // 채팅 확인 필요 목록은 1) plan: master 우선 2) lastMessageSentAt이 오래된 순서대로(오름차순) 정렬
  chatList.sort((a, b) => {
    const planRank = (p?: PatientType["plan"]) => {
      const key = (p ? String(p) : "").toLowerCase();
      // master가 최우선, 그 외(challenger, starter, undefined 등)는 동일 순위
      return key === "master" ? 0 : 1;
    };
    const parse = (s?: string) => {
      const t = s ? Date.parse(s) : NaN;
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    const planDiff = planRank(a.plan) - planRank(b.plan);
    if (planDiff !== 0) return planDiff;
    return parse(a.lastMessageSentAt) - parse(b.lastMessageSentAt);
  });

  // 2. 휴먼 체크 필요 (Priority 2)
  const humanCheckList = createPrioritizedList((p) => !!p.isHumanCheckRequired);

  // 3. 위험 체크 필요 (Priority 3)
  const riskyList = createPrioritizedList((p) => !!p.isRiskCheckRequired);

  // 4. 만료 안내 필요 (Priority 4)
  const expirationNoticeRequiredList = createPrioritizedList(
    (p) => !!p.isExpirationNoticeRequired
  );

  // 5. 업데이트 필요 (Priority 5)
  // 이 목록은 자체 정렬 규칙이 있으므로 별도 처리합니다.
  let updateList = createPrioritizedList(
    (p) =>
      !!p.isTaskUpdateRequired &&
      !p.isHumanCheckRequired && // 이 조건들은 상위 우선순위에서 이미 걸러졌지만, 명시적으로 남겨둘 수 있습니다.
      !p.isRiskCheckRequired &&
      (p.unreadMessageCount || 0) === 0
  );
  // 정렬 우선순위:
  // 1) (totalUpdateCount - currentUpdateCount) 내림차순
  // 2) plan: master -> challenger -> starter
  // 3) doneAt 오래된 순
  const planOrder = (plan?: PatientType["plan"]) => {
    const key = (plan ? String(plan) : "").toLowerCase();
    if (key === "master") return 0;
    if (key === "challenger") return 1;
    if (key === "starter") return 2;
    return 1;
  };
  const getDelta = (p: PatientType) =>
    (p.totalUpdateCount ?? 0) - (p.currentUpdateCount ?? 0);
  const getDoneAtTime = (s?: string) => {
    const t = s ? Date.parse(s) : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  updateList.sort((a, b) => {
    // 1) delta desc
    const deltaDiff = getDelta(b) - getDelta(a);
    if (deltaDiff !== 0) return deltaDiff;

    // 2) plan order asc by rank
    const planDiff = planOrder(a.plan) - planOrder(b.plan);
    if (planDiff !== 0) return planDiff;

    // 3) doneAt older first (ascending timestamp)
    return getDoneAtTime(a.doneAt) - getDoneAtTime(b.doneAt);
  });

  // 6. 리마인드 필요 (Priority 6)
  const remindList = createPrioritizedList(
    (p) =>
      !!p.isRemindRequired &&
      !p.isRiskCheckRequired &&
      (p.unreadMessageCount || 0) === 0
  );

  return [
    {
      listTitle: "채팅 확인 필요",
      patientList: chatList,
      menuKey: "patientList_isChatCheckRequired",
    },
    {
      listTitle: "휴먼 체크 필요",
      patientList: humanCheckList,
      menuKey: "patientList_humanCheckRequired",
    },
    {
      listTitle: "위험 체크 필요",
      patientList: riskyList,
      menuKey: "patientList_isRiskCheckRequired",
    },
    {
      listTitle: "만료 안내 필요",
      patientList: expirationNoticeRequiredList,
      menuKey: "patientList_isExpirationNoticeRequired",
    },
    {
      listTitle: "업데이트 필요",
      patientList: updateList,
      menuKey: "patientList_taskOpenRequired",
    },
    {
      listTitle: "리마인드 필요",
      patientList: remindList,
      menuKey: "patientList_isRemindRequired",
    },
    {
      listTitle: "전체",
      patientList: sortedByNickname,
      menuKey: "patientList_all",
    },
  ];
};

// 환불 관리 계정(admin && userId 2739)은 우선순위 목록 없이 환불자 전체 단일 목록만 사용
const REFUND_ADMIN_USER_ID = 2739;
const REFUND_ADMIN_LIST_TITLE = "환불자 전체";

const categorizePatientsForRefundAdmin = (
  patients: PatientType[]
): CategorizedPatientList[] => {
  const getStoppedAtTime = (s?: string) => {
    const t = s ? Date.parse(s) : NaN;
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
  };
  // programStoppedAt 내림차순 (없는 경우 맨 뒤)
  const sortedByStoppedAt = [...patients].sort(
    (a, b) =>
      getStoppedAtTime(b.programStoppedAt) -
      getStoppedAtTime(a.programStoppedAt)
  );
  return [
    {
      listTitle: REFUND_ADMIN_LIST_TITLE,
      patientList: sortedByStoppedAt,
      menuKey: "patientList_all",
    },
  ];
};

export default function PatientList({
  selectedCoach,
  setCoachList,
  setCurrentMenu,
  backgroundColor,
}: {
  selectedCoach?: string;
  setCoachList: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentMenu?: React.Dispatch<React.SetStateAction<SidebarMenuType>>;
  backgroundColor: string;
}) {
  const [patientState, setPatientState] = useRecoilState(patientAtom);
  const user = useRecoilValue(userAtom);
  const isRefundAdmin =
    user?.role === "admin" && Number(user?.userId) === REFUND_ADMIN_USER_ID;

  const { isLoading } = useQuery(
    [PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT],
    () => patientApi.getPatientList(),
    {
      enabled: hasAuthHeader(),
      refetchInterval: 10000,
      onSuccess: (data) => {
        if (data && data.result) {
          const list = data.result;

          // 코치 목록 생성 로직은 그대로 유지
          const coachSet = new Set(list.map((each) => each.coachName || ""));
          const coachArray = Array.from(coachSet);
          setCoachList([...coachArray]);

          // 분리된 헬퍼 함수를 호출하여 우선순위가 적용된 목록을 생성
          const categorizedLists = isRefundAdmin
            ? categorizePatientsForRefundAdmin(list)
            : categorizePatientsByPriority(list);
          setTotalListData(categorizedLists);
        }
      },
    }
  );

  const [totalListData, setTotalListData] = useState<
    CategorizedPatientList[] | undefined
  >();
  const [listData, setListData] = useState<
    CategorizedPatientList[] | undefined
  >();

  useEffect(() => {
    if (totalListData) {
      if (selectedCoach) {
        setListData(
          totalListData?.map((each) => ({
            ...each,
            patientList: each.patientList.filter((element) =>
              !selectedCoach ? true : element.coachName === selectedCoach
            ),
          }))
        );
      } else {
        setListData(totalListData);
      }

      if (patientState?.patientId) {
        const allPatients =
          totalListData.find((list) => list.menuKey === "patientList_all")
            ?.patientList || [];
        const updatedPatient = allPatients.find(
          (patient) => patient.patientId === patientState.patientId
        );

        if (updatedPatient) {
          setPatientState(updatedPatient);
        }
      }
    }
  }, [selectedCoach, totalListData, patientState?.patientId, setPatientState]);

  const [selected, setSelected] = useState<string | undefined>();

  useEffect(() => {
    if (selected && listData) {
      const selectedTitle = selected.split("_")[0];
      const selectedPid = Number(selected.split("_")[1]);
      if (
        !listData
          .find((element) => element.listTitle === selectedTitle)
          ?.patientList.map((each) => each.patientId)
          .includes(selectedPid)
      ) {
        setSelected(
          `${isRefundAdmin ? REFUND_ADMIN_LIST_TITLE : "전체"}_${selectedPid}`
        );
      }
    }
  }, [listData, selected, isRefundAdmin]);

  return isLoading ? (
    <Sheet sx={{ backgroundColor: "transparent", height: "100%" }}>
      <Stack
        alignItems={"center"}
        justifyContent="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress color="neutral" />
      </Stack>
    </Sheet>
  ) : (
    <Sheet sx={{ backgroundColor: "transparent" }}>
      {listData && (
        <Box
          sx={{
            minHeight: 0,
            overflow: "visible",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <List sx={{ py: 0, gap: "24px" }}>
            {listData.map(
              ({ menuKey, listTitle, patientList }) =>
                patientList &&
                patientList.length > 0 && (
                  <PatientSubList
                    key={listTitle}
                    menuKey={menuKey}
                    title={listTitle}
                    patientList={patientList}
                    selected={selected}
                    setSelected={setSelected}
                    setCurrentMenu={setCurrentMenu}
                  />
                )
            )}
          </List>
        </Box>
      )}
    </Sheet>
  );
}
